import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Activity, Code2, KeyRound, Play, Plus, Rocket, Trash2 } from "lucide-react";
import {
  api,
  type EdgeFunction,
  type EdgeFunctionInvocation,
  type EdgeFunctionSecret,
  type EdgeFunctionStats,
  type EdgeFunctionTestResult,
} from "@/lib/api";
import { EmptyState, ErrorBox, PageHeader, Spinner, StatTile } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STARTER_SOURCE = `module.exports = async function (request, env, context) {
  console.info("invoking", context.functionName);

  return new Response({
    message: "Hello from TinyBase Edge Functions!",
    method: request.method,
    userId: request.userId || null
  }, {
    status: 200,
    headers: { "Content-Type": "application/json" }
  });
};`;

function statusVariant(status: EdgeFunction["status"]) {
  if (status === "active") return "success" as const;
  if (status === "disabled") return "warning" as const;
  return "secondary" as const;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
}

export function EdgeFunctionsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [functions, setFunctions] = useState<EdgeFunction[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [selected, setSelected] = useState<EdgeFunction | null>(null);
  const [stats, setStats] = useState<EdgeFunctionStats | null>(null);
  const [invocations, setInvocations] = useState<EdgeFunctionInvocation[]>([]);
  const [secrets, setSecrets] = useState<EdgeFunctionSecret[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("Hello world");
  const [createSlug, setCreateSlug] = useState("hello-world");
  const [createDescription, setCreateDescription] = useState("");
  const [source, setSource] = useState(STARTER_SOURCE);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [secretName, setSecretName] = useState("");
  const [secretValue, setSecretValue] = useState("");
  const [invokeBody, setInvokeBody] = useState(`{"name":"TinyBase"}`);
  const [invokeResult, setInvokeResult] = useState<EdgeFunctionTestResult | null>(null);

  const loadOverview = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      const [functionResult, statsResult, secretResult] = await Promise.all([
        api.listEdgeFunctions(id),
        api.edgeFunctionStats(id),
        api.listEdgeFunctionSecrets(id),
      ]);
      const list = functionResult.data ?? [];
      setFunctions(list);
      setStats(statsResult.data);
      setSecrets(secretResult.data ?? []);
      setSelectedId((current) =>
        current && list.some((item) => item.id === current)
          ? current
          : (list[0]?.id ?? ""),
      );
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadSelected = useCallback(async () => {
    if (!id || !selectedId) {
      setSelected(null);
      setInvocations([]);
      return;
    }
    setError(null);
    try {
      const [functionResult, invocationResult] = await Promise.all([
        api.getEdgeFunction(id, selectedId),
        api.listEdgeFunctionInvocations(id, selectedId),
      ]);
      setSelected(functionResult.data);
      setName(functionResult.data.name);
      setDescription(functionResult.data.description ?? "");
      setSource(functionResult.data.source ?? STARTER_SOURCE);
      setInvocations(invocationResult.data ?? []);
    } catch (e) {
      setError(e);
    }
  }, [id, selectedId]);

  useEffect(() => { void loadOverview(); }, [loadOverview]);
  useEffect(() => { void loadSelected(); }, [loadSelected]);

  const errorRate = useMemo(() => {
    if (!stats?.invocations) return "0.0%";
    return `${((stats.errors / stats.invocations) * 100).toFixed(1)}%`;
  }, [stats]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try { await action(); } catch (e) { setError(e); } finally { setBusy(false); }
  }

  async function createFunction() {
    await run(async () => {
      const result = await api.createEdgeFunction(id, {
        name: createName.trim(),
        slug: createSlug.trim().toLowerCase(),
        description: createDescription.trim(),
        source,
      });
      setShowCreate(false);
      await loadOverview();
      setSelectedId(result.data.id);
    });
  }

  async function saveMetadata() {
    if (!selected) return;
    await run(async () => {
      await api.updateEdgeFunction(id, selected.id, { name, description });
      await loadOverview();
      await loadSelected();
    });
  }

  async function deploy() {
    if (!selected) return;
    await run(async () => {
      await api.deployEdgeFunction(id, selected.id, source);
      await loadOverview();
      await loadSelected();
    });
  }

  async function toggleEnabled() {
    if (!selected) return;
    await run(async () => {
      await api.setEdgeFunctionEnabled(id, selected.id, selected.status !== "active");
      await loadOverview();
      await loadSelected();
    });
  }

  async function removeFunction() {
    if (!selected || !confirm(`Delete edge function ${selected.slug} and all of its logs?`)) return;
    await run(async () => {
      await api.deleteEdgeFunction(id, selected.id);
      setSelectedId("");
      setSelected(null);
      await loadOverview();
    });
  }

  async function saveSecret() {
    if (!secretName.trim() || !secretValue) return;
    await run(async () => {
      await api.putEdgeFunctionSecret(id, secretName.trim().toUpperCase(), secretValue);
      setSecretName("");
      setSecretValue("");
      const result = await api.listEdgeFunctionSecrets(id);
      setSecrets(result.data ?? []);
    });
  }

  async function deleteSecret(secret: EdgeFunctionSecret) {
    if (!confirm(`Delete secret ${secret.name}?`)) return;
    await run(async () => {
      await api.deleteEdgeFunctionSecret(id, secret.name);
      setSecrets((current) => current.filter((item) => item.name !== secret.name));
    });
  }

  async function invoke() {
    if (!selected) return;
    await run(async () => {
      const result = await api.invokeEdgeFunction(id, selected.id, invokeBody);
      setInvokeResult(result);
      await Promise.all([loadSelected(), loadOverview()]);
    });
  }

  if (loading) return <Spinner label="Loading Edge Functions…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Compute"
        title="Edge Functions"
        description="Deploy project-scoped JavaScript, manage encrypted secrets, test invocations, and inspect durable usage and logs. Each invocation runs in a fresh bounded VM."
        actions={<Button size="sm" onClick={() => { setShowCreate(true); setSelected(null); setSource(STARTER_SOURCE); }}><Plus /> New function</Button>}
      />

      {error ? <div className="mb-5"><ErrorBox error={error} /></div> : null}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Invocations (24h)" value={stats?.invocations ?? 0} hint={`${stats?.monthly_invocation_limit.toLocaleString() ?? 0} monthly limit`} />
        <StatTile label="Error rate" value={errorRate} hint={`${stats?.errors ?? 0} failed`} />
        <StatTile label="P95 duration" value={`${(stats?.p95_duration_ms ?? 0).toFixed(1)} ms`} hint={`P50 ${(stats?.p50_duration_ms ?? 0).toFixed(1)} ms`} />
        <StatTile label="Compute" value={`${stats?.compute_ms ?? 0} ms`} hint={`${formatBytes(stats?.response_bytes ?? 0)} response egress`} />
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <aside className="card p-3">
          <div className="mb-3 flex items-center justify-between px-2">
            <h2 className="text-sm font-semibold">Functions</h2>
            <Badge variant="secondary">{functions.length}</Badge>
          </div>
          {functions.length === 0 ? (
            <p className="px-2 py-8 text-center text-sm text-text-muted">No functions deployed.</p>
          ) : (
            <ul className="space-y-1">
              {functions.map((fn) => (
                <li key={fn.id}>
                  <button
                    type="button"
                    onClick={() => { setShowCreate(false); setSelectedId(fn.id); }}
                    className={`w-full rounded-sm border px-3 py-2 text-left ${selectedId === fn.id && !showCreate ? "border-secondary bg-secondary-fixed" : "border-transparent hover:bg-surface-container-low"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold">{fn.name}</span>
                      <Badge variant={statusVariant(fn.status)}>{fn.status}</Badge>
                    </div>
                    <div className="mt-1 truncate font-mono text-[11px] text-text-muted">/{fn.slug} · v{fn.active_version ?? "–"}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {showCreate ? (
          <section className="card space-y-4 p-5">
            <div><h2 className="text-base font-semibold">Create and deploy</h2><p className="mt-1 text-xs text-text-muted">The initial source is compiled and activated as version 1 atomically.</p></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs text-text-muted">Name<input className="input mt-1" value={createName} onChange={(e) => setCreateName(e.target.value)} /></label>
              <label className="text-xs text-text-muted">Slug<input className="input mt-1 font-mono" value={createSlug} onChange={(e) => setCreateSlug(e.target.value)} /></label>
            </div>
            <label className="block text-xs text-text-muted">Description<input className="input mt-1" value={createDescription} onChange={(e) => setCreateDescription(e.target.value)} /></label>
            <SourceEditor value={source} onChange={setSource} />
            <div className="flex gap-2"><Button disabled={busy} onClick={() => void createFunction()}><Rocket /> Create & deploy</Button><Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button></div>
          </section>
        ) : selected ? (
          <section className="space-y-5">
            <div className="card space-y-4 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><h2 className="text-base font-semibold">{selected.name}</h2><code className="text-xs text-text-muted">POST /v1/functions/{selected.slug}</code></div>
                <div className="flex gap-2"><Button size="sm" variant="outline" disabled={busy} onClick={() => void toggleEnabled()}>{selected.status === "active" ? "Disable" : "Enable"}</Button><Button size="sm" variant="destructive" disabled={busy} onClick={() => void removeFunction()}><Trash2 /> Delete</Button></div>
              </div>
              <div className="grid gap-3 sm:grid-cols-[1fr_2fr_auto]">
                <label className="text-xs text-text-muted">Name<input className="input mt-1" value={name} onChange={(e) => setName(e.target.value)} /></label>
                <label className="text-xs text-text-muted">Description<input className="input mt-1" value={description} onChange={(e) => setDescription(e.target.value)} /></label>
                <Button className="self-end" variant="outline" disabled={busy} onClick={() => void saveMetadata()}>Save details</Button>
              </div>
              <SourceEditor value={source} onChange={setSource} />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-[11px] text-text-muted">active v{selected.active_version ?? "–"} · sha {selected.source_sha256?.slice(0, 12) ?? "–"}</span>
                <Button disabled={busy} onClick={() => void deploy()}><Rocket /> Compile & deploy new version</Button>
              </div>
            </div>

            <div className="card space-y-3 p-5">
              <div className="flex items-center gap-2"><Play className="h-4 w-4 text-secondary" /><h2 className="text-sm font-semibold">Invocation tester</h2></div>
              <textarea className="input min-h-24 resize-y font-mono text-xs" value={invokeBody} onChange={(e) => setInvokeBody(e.target.value)} aria-label="Invocation JSON body" />
              <Button size="sm" disabled={busy || selected.status !== "active"} onClick={() => void invoke()}><Play /> Invoke POST</Button>
              {invokeResult ? (
                <div className="rounded-sm bg-code-block-bg p-3 font-mono text-xs text-code-block-text">
                  <div className="mb-2 text-[11px] text-text-disabled">HTTP {invokeResult.status} · v{invokeResult.version ?? "?"} · invocation {invokeResult.invocation_id ?? "?"}</div>
                  <pre className="whitespace-pre-wrap break-all">{invokeResult.body || "(empty body)"}</pre>
                </div>
              ) : null}
            </div>
          </section>
        ) : (
          <EmptyState title="Select or create a function" icon={<Code2 className="h-5 w-5" />}>Choose a deployment to edit code, invoke it, and inspect logs.</EmptyState>
        )}
      </div>

      <section className="card mb-6 p-5">
        <div className="mb-4 flex items-center gap-2"><KeyRound className="h-4 w-4 text-secondary" /><div><h2 className="text-sm font-semibold">Project secrets</h2><p className="text-xs text-text-muted">Values are encrypted at rest, injected as <code>env.NAME</code>, and never returned by the API.</p></div></div>
        <div className="mb-4 grid gap-2 sm:grid-cols-[14rem_1fr_auto]">
          <input className="input font-mono" placeholder="API_TOKEN" value={secretName} onChange={(e) => setSecretName(e.target.value.toUpperCase())} />
          <input className="input font-mono" type="password" placeholder="Secret value" value={secretValue} onChange={(e) => setSecretValue(e.target.value)} />
          <Button variant="outline" disabled={busy || !secretName || !secretValue} onClick={() => void saveSecret()}>Save secret</Button>
        </div>
        {secrets.length === 0 ? <p className="text-sm text-text-muted">No secrets configured.</p> : (
          <div className="flex flex-wrap gap-2">{secrets.map((secret) => <div key={secret.name} className="flex items-center gap-2 rounded-sm border border-border-standard px-3 py-2"><code className="text-xs">{secret.name}</code><span className="text-xs text-text-disabled">••••••••</span><button type="button" className="text-error" onClick={() => void deleteSecret(secret)} aria-label={`Delete ${secret.name}`}><Trash2 className="h-3.5 w-3.5" /></button></div>)}</div>
        )}
      </section>

      <section className="card p-5">
        <div className="mb-4 flex items-center justify-between"><div className="flex items-center gap-2"><Activity className="h-4 w-4 text-secondary" /><div><h2 className="text-sm font-semibold">Invocation log</h2><p className="text-xs text-text-muted">Latest durable outcomes and user console output for the selected function.</p></div></div><Badge variant="secondary">{invocations.length}</Badge></div>
        {invocations.length === 0 ? <p className="text-sm text-text-muted">No invocations for this function yet.</p> : (
          <div className="space-y-2">{invocations.map((invocation) => (
            <article key={invocation.id} className="rounded-sm border border-border-standard p-3">
              <div className="flex flex-wrap items-center gap-2 text-xs"><Badge variant={invocation.outcome === "success" ? "success" : "danger"}>{invocation.outcome}</Badge><code>{invocation.status_code}</code><span>{invocation.duration_ms} ms</span><span>v{invocation.version}</span><span className="ml-auto text-text-muted">{new Date(invocation.invoked_at).toLocaleString()}</span></div>
              {invocation.error_message ? <p className="mt-2 text-xs text-error">{invocation.error_message}</p> : null}
              {invocation.logs.length ? <ul className="mt-2 space-y-1 rounded-sm bg-code-block-bg p-2 font-mono text-[11px] text-code-block-text">{invocation.logs.map((log, index) => <li key={`${log.created_at}-${index}`}><span className="mr-2 text-text-disabled">{log.level}</span>{log.message}</li>)}</ul> : null}
            </article>
          ))}</div>
        )}
      </section>
    </div>
  );
}

function SourceEditor({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-xs text-text-muted">
      JavaScript source · CommonJS <code>module.exports = function</code>
      <textarea
        className="mt-1 min-h-[22rem] w-full resize-y rounded-sm border border-code-block-border bg-code-block-bg p-4 font-mono text-xs leading-5 text-code-block-text focus:border-secondary"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        spellCheck={false}
        aria-label="Edge function JavaScript source"
      />
    </label>
  );
}
